import React, { useMemo } from 'react';

/**
 * CashKeypad — teclado de caja (efectivo) del Centro de Pagos.
 * UI autocontenida: maneja el string de dígitos y calcula el cambio.
 * Conéctalo a tu lógica pasando value/onChange (controlado) o déjalo interno.
 *
 * @param {number}  total            Total a pagar (para calcular cambio y validar).
 * @param {string}  [value]          Monto recibido como string de centavos-libre (controlado).
 * @param {(v:string)=>void} [onChange]  Setter del monto (controlado).
 * @param {string[]} [quickAmounts]  Botones rápidos. Default $1..$100.
 * @param {(amount:number)=>void} [onConfirm]  Se llama al confirmar (tú disparas processPayment()).
 */
export function CashKeypad({ total = 0, value, onChange, quickAmounts, onConfirm }) {
   const [inner, setInner] = React.useState('');
   const raw = value !== undefined ? value : inner;
   const setRaw = (v) => (onChange ? onChange(v) : setInner(v));

   const amount = useMemo(() => Number(raw || 0), [raw]);
   const change = useMemo(() => amount - Number(total || 0), [amount, total]);
   const enough = amount >= Number(total || 0) && amount > 0;

   const quicks = quickAmounts || ['1', '5', '10', '20', '50', '100'];

   const pressDigit = (d) => {
      // evita múltiples puntos y limita a 2 decimales
      if (d === '.' && raw.includes('.')) return;
      if (raw.includes('.') && raw.split('.')[1]?.length >= 2 && d !== '.') return;
      setRaw((raw === '0' && d !== '.') ? d : raw + d);
   };
   const press00 = () => { if (raw === '' || raw === '0') { setRaw('0'); return; } if (raw.includes('.') && raw.split('.')[1]?.length >= 1) return; setRaw(raw + '00'); };
   const backspace = () => setRaw(raw.slice(0, -1));
   const setQuick = (q) => setRaw(String((Number(raw || 0) + Number(q))));
   const display = raw === '' ? '0.00' : (raw.includes('.') ? raw : Number(raw).toString());

   return (
      <div className="sp-kp">
         {/* Display */}
         <div className="sp-kp__display">
            <span className="sp-kp__cur">$</span>
            <span className="sp-kp__amount" translate="no">{display}</span>
            <span className="sp-kp__caret" aria-hidden="true"></span>
         </div>

         {/* Cambio */}
         {amount > 0 && (
            <div className={`sp-kp__change ${enough ? 'is-ok' : 'is-short'}`}>
               {enough
                  ? <>Cambio: <strong translate="no">$ {change.toFixed(2)}</strong></>
                  : <>Falta: <strong translate="no">$ {Math.abs(change).toFixed(2)}</strong></>}
            </div>
         )}

         {/* Montos rápidos */}
         <div className="sp-kp__quick">
            {quicks.map(q => (
               <button key={q} type="button" className="sp-kp__quickbtn" onClick={() => setQuick(q)}>${q}</button>
            ))}
         </div>

         {/* Teclado */}
         <div className="sp-kp__pad">
            {['1','2','3','4','5','6','7','8','9'].map(n => (
               <button key={n} type="button" className="sp-kp__key" onClick={() => pressDigit(n)}>{n}</button>
            ))}
            <button type="button" className="sp-kp__key" onClick={press00}>00</button>
            <button type="button" className="sp-kp__key" onClick={() => pressDigit('0')}>0</button>
            <button type="button" className="sp-kp__key sp-kp__key--back" onClick={backspace} aria-label="Borrar">
               <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 4H8l-7 8 7 8h13a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1z" /><line x1="18" y1="9" x2="12" y2="15" /><line x1="12" y1="9" x2="18" y2="15" /></svg>
            </button>
         </div>
      </div>
   );
}
export default CashKeypad;